import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents, waitForCartUiRendered } from '@theme/events';
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  static #nearViewportPreloadCount = 0;
  static #nearViewportPreloadLimit = 2;
  static #preloadQueue = Promise.resolve();
  static #activeModalRequestId = 0;
  /** @type {QuickAddComponent | null} */
  static #activeModalComponent = null;
  static #preloadObserver = new IntersectionObserver(
    (entries, observer) => {
      if (QuickAddComponent.#nearViewportPreloadCount >= QuickAddComponent.#nearViewportPreloadLimit) return;

      const candidates = entries
        .filter((entry) => entry.isIntersecting && entry.target instanceof QuickAddComponent)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      for (const candidate of candidates) {
        if (QuickAddComponent.#nearViewportPreloadCount >= QuickAddComponent.#nearViewportPreloadLimit) break;
        if (!(candidate.target instanceof QuickAddComponent)) continue;

        QuickAddComponent.#nearViewportPreloadCount += 1;
        observer.unobserve(candidate.target);
        QuickAddComponent.#preloadQueue = QuickAddComponent.#preloadQueue.then(() =>
          candidate.target.#preloadProductPage()
        );
      }

      if (QuickAddComponent.#nearViewportPreloadCount >= QuickAddComponent.#nearViewportPreloadLimit) {
        observer.disconnect();
      }
    },
    { rootMargin: '300px 0px' }
  );

  /** @param {QuickAddComponent} component */
  static #beginModalRequest(component) {
    QuickAddComponent.#activeModalComponent?.#abortController?.abort();
    QuickAddComponent.#activeModalComponent = component;
    QuickAddComponent.#activeModalRequestId += 1;
    return QuickAddComponent.#activeModalRequestId;
  }

  /** @param {number} requestId */
  static #ownsModalRequest(requestId) {
    return QuickAddComponent.#activeModalRequestId === requestId;
  }

  static invalidateModalRequest() {
    QuickAddComponent.#activeModalComponent?.#abortController?.abort();
    QuickAddComponent.#activeModalComponent = null;
    QuickAddComponent.#activeModalRequestId += 1;
  }

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink?.() || hotspotProduct?.getHotspotProductLink?.();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId?.() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    QuickAddComponent.#preloadObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    QuickAddComponent.#preloadObserver.unobserve(this);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#abortController?.abort();
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  };

  async #preloadProductPage() {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#pendingContent.has(currentUrl)) return;

    try {
      await this.#loadProductGrid(currentUrl);
    } catch {
      // Speculative loading must never surface as an interaction error.
    }
  }

  /**
   * Shares speculative and click-triggered product requests.
   * @param {string} currentUrl
   * @returns {Promise<Element | null>}
   */
  async #loadProductGrid(currentUrl) {
    const cached = this.#cachedContent.get(currentUrl);
    if (cached) return cached;

    const pending = this.#pendingContent.get(currentUrl);
    if (pending) return pending;

    const request = this.fetchProductPage(currentUrl).then((html) => {
      const gridElement = html?.querySelector('[data-product-grid-content]');
      if (!gridElement) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(currentUrl, productGrid);
      return productGrid;
    });
    this.#pendingContent.set(currentUrl, request);

    try {
      return await request;
    } finally {
      if (this.#pendingContent.get(currentUrl) === request) {
        this.#pendingContent.delete(currentUrl);
      }
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    // NUO compatibility guard: updater or app markup can temporarily omit the expected method.
    if (!variantPicker || typeof variantPicker.updateVariantPicker !== 'function') return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const trigger =
      event.composedPath().find((node) => node instanceof HTMLButtonElement) ||
      this.querySelector('.quick-add__button--choose');
    const currentUrl = this.productPageUrl;
    const requestId = QuickAddComponent.#beginModalRequest(this);

    this.#prepareLoadingState();
    this.#setTriggerLoading(trigger, true);

    try {
      const productGrid = await this.#loadProductGrid(currentUrl);
      if (!QuickAddComponent.#ownsModalRequest(requestId)) return;

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
        // NUO: after the pickers settle on the server-rendered default, the shade
        // guard unchecks it unless the shade was deliberately chosen by the customer.
        window.NuoShadeGuard?.apply(document.getElementById('quick-add-modal-content'), this.#getSelectedVariantId());
        await this.#openQuickAddModal();
      }
    } catch (error) {
      if (!QuickAddComponent.#ownsModalRequest(requestId)) return;
      throw error;
    } finally {
      this.#setTriggerLoading(trigger, false);
    }
  };

  #prepareLoadingState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent instanceof HTMLElement) modalContent.replaceChildren();
  }

  /** @param {HTMLButtonElement | null} trigger @param {boolean} isLoading */
  #setTriggerLoading(trigger, isLoading) {
    if (!trigger) return;
    trigger.disabled = isLoading;
    if (isLoading) {
      trigger.dataset.nuoPending = 'true';
      trigger.setAttribute('aria-busy', 'true');
    } else {
      trigger.removeAttribute('data-nuo-pending');
      trigger.removeAttribute('aria-busy');
    }
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const dialog = dialogComponent.refs?.dialog;
    const loading = dialogComponent.querySelector('[data-quick-add-loading]');
    if (!(dialog instanceof HTMLDialogElement) || !(loading instanceof HTMLElement)) return;

    dialog.setAttribute('aria-busy', String(isLoading));
    loading.hidden = !isLoading;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = async () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return false;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return false;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
    const opened = dialog.open
      ? Promise.resolve()
      : new Promise((resolve) => dialogComponent.addEventListener(DialogOpenEvent.eventName, resolve, { once: true }));
    dialogComponent.showDialog();
    await opened;
    return true;
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const requestUrl = new URL(productPageUrl, window.location.origin);
      requestUrl.searchParams.set('view', 'quick-add');
      const response = await fetch(requestUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    try {
      this.#syncVariantSelection(modalContent);
    } catch (error) {
      // NUO: opening the chooser is safer than blocking quick-add on a transient sync mismatch.
      console.warn('Quick add variant sync failed; opening modal with default selection.', error);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    const cartUiRendered = waitForCartUiRendered(event);
    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;
        const renderResult = await cartUiRendered;
        if (!renderResult.ok) {
          console.warn(`[quick-add] Cart UI handoff failed: ${renderResult.reason}`);
          return;
        }
        await this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    QuickAddComponent.invalidateModalRequest();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
